import { z } from "zod"

import { commandEnvelopeV1Schema } from "./command-envelope-v1.js"
import {
  FIELD_BEFORE_AFTER_VIDEO_MAXIMUM_BYTES,
  FIELD_BEFORE_AFTER_VIDEO_MAXIMUM_DURATION_MS,
} from "./field-before-after-v2.js"
import {
  fieldBeforeAfterRoleV1Schema,
  fieldBeforeAfterSourceV1Schema,
} from "./field-before-after-v1.js"
import {
  FIELD_MEDIA_CHUNK_BYTES,
  FIELD_MEDIA_MAXIMUM_BYTES,
  fieldMediaUploadInitiateV1Schema,
} from "./field-media-v1.js"
import { fieldMediaUploadInitiateV2Schema } from "./field-media-v2.js"
import { entityIdSchema } from "./identifiers.js"
import { fieldMediaUploadInitiateV4Schema } from "./field-media-v4.js"

export const FIELD_MEDIA_VIDEO_CHUNK_BYTES = 4 * 1024 * 1024

export const fieldMediaUploadInitiateV3Schema = z
  .object({
    requestVersion: z.literal(3),
    mediaId: entityIdSchema,
    visitId: entityIdSchema,
    beforeAfterSetId: entityIdSchema,
    role: fieldBeforeAfterRoleV1Schema,
    source: fieldBeforeAfterSourceV1Schema,
    fileName: z
      .string()
      .min(1)
      .max(128)
      .regex(/^[A-Za-z0-9][A-Za-z0-9._ -]*$/),
    mediaType: z.enum(["image/jpeg", "image/png", "video/mp4"]),
    sizeBytes: z
      .number()
      .int()
      .min(1)
      .max(FIELD_BEFORE_AFTER_VIDEO_MAXIMUM_BYTES)
      .safe(),
    sha256: z.string().regex(/^[0-9a-f]{64}$/),
    capturedAt: z.iso.datetime({ offset: true }),
    durationMs: z
      .number()
      .int()
      .min(1)
      .max(FIELD_BEFORE_AFTER_VIDEO_MAXIMUM_DURATION_MS)
      .safe()
      .nullable(),
    associationCommand: commandEnvelopeV1Schema,
  })
  .strict()
  .superRefine((request, context) => {
    const isVideo = request.mediaType === "video/mp4"
    if (
      isVideo !== (request.durationMs !== null) ||
      (!isVideo && request.sizeBytes > FIELD_MEDIA_MAXIMUM_BYTES)
    ) {
      context.addIssue({
        code: "custom",
        path: ["durationMs"],
        message: "Media size and duration must match its type.",
      })
    }
    const command = request.associationCommand
    const payload = command.payload
    if (
      command.commandType !== "visit.media.associate" ||
      command.commandVersion !== 3 ||
      command.context.entityType !== "visit" ||
      command.context.entityId !== request.visitId ||
      command.expectedVersion === null ||
      payload.mediaId !== request.mediaId ||
      payload.visitId !== request.visitId ||
      payload.beforeAfterSetId !== request.beforeAfterSetId ||
      payload.role !== request.role ||
      payload.source !== request.source ||
      payload.fileName !== request.fileName ||
      payload.mediaType !== request.mediaType ||
      payload.sizeBytes !== request.sizeBytes ||
      payload.sha256 !== request.sha256 ||
      payload.capturedAt !== request.capturedAt ||
      payload.durationMs !== request.durationMs ||
      payload.storageReference !== `field-media:${request.mediaId}`
    ) {
      context.addIssue({
        code: "custom",
        path: ["associationCommand"],
        message: "Before/After media association must match the upload.",
      })
    }
  })

export const fieldMediaUploadInitiateSchema = z.union([
  fieldMediaUploadInitiateV1Schema,
  fieldMediaUploadInitiateV2Schema,
  fieldMediaUploadInitiateV3Schema,
  fieldMediaUploadInitiateV4Schema,
])

export const fieldMediaUploadSessionV2Schema = z
  .object({
    responseVersion: z.literal(2),
    uploadId: entityIdSchema,
    mediaId: entityIdSchema,
    resumeToken: z.string().min(32).max(512),
    nextOffset: z
      .number()
      .int()
      .min(0)
      .max(FIELD_BEFORE_AFTER_VIDEO_MAXIMUM_BYTES)
      .safe(),
    chunkSizeBytes: z.union([
      z.literal(FIELD_MEDIA_CHUNK_BYTES),
      z.literal(FIELD_MEDIA_VIDEO_CHUNK_BYTES),
    ]),
    expiresAt: z.iso.datetime({ offset: true }),
  })
  .strict()

export const fieldMediaUploadResultV2Schema = z
  .object({
    responseVersion: z.literal(2),
    uploadId: entityIdSchema,
    mediaId: entityIdSchema,
    status: z.enum(["uploading", "scanned", "associated"]),
    nextOffset: z
      .number()
      .int()
      .min(0)
      .max(FIELD_BEFORE_AFTER_VIDEO_MAXIMUM_BYTES)
      .safe(),
    storageReference: z
      .string()
      .regex(/^field-media:[0-9a-f-]{36}$/)
      .nullable(),
  })
  .strict()

export type FieldMediaUploadInitiateV3 = z.infer<
  typeof fieldMediaUploadInitiateV3Schema
>
export type FieldMediaUploadInitiate = z.infer<
  typeof fieldMediaUploadInitiateSchema
>
export type FieldMediaUploadSessionV2 = z.infer<
  typeof fieldMediaUploadSessionV2Schema
>
export type FieldMediaUploadSession = FieldMediaUploadSessionV2
export type FieldMediaUploadResultV2 = z.infer<
  typeof fieldMediaUploadResultV2Schema
>
